import type { EntityCollectionState } from './entitySliceTypes';
import type { EntityEvent, EntityRecord } from './entityTypes';
import { mergeAlbumRecord, mergeArtistRecord, mergePersonRecord } from './mergeEntity';

function normalizeNameForCompare(v: unknown): string {
  return String(v || '')
    .trim()
    .toLowerCase()
    // Normalize diacritics so "Béla" and "Bela" match (prevents stale-event drops).
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/\s+/g, ' ')
    .replace(/^\s*[-–—]+\s*/, '');
}

function ensureRecord(
  entityType: EntityRecord['entityType'],
  key: string,
  prev: EntityCollectionState
): EntityRecord | null {
  return prev.byKey[key] || null;
}

function makeSkeleton(
  entityType: EntityRecord['entityType'],
  key: string,
  id: string | null | undefined,
  name: string | null | undefined,
  requestId: string,
  ts: number,
  hintKind?: EntityRecord['kind']
): EntityRecord {
  const baseData: Record<string, any> =
    entityType === 'album'
      ? { id: id || null, title: name || null }
      : { id: id || null, name: name || null };

  return {
    key,
    entityType,
    id: id || null,
    name: name || null,
    kind: hintKind || 'unknown',
    data: baseData,
    fields: {},
    sourcesSeen: { loading: ts },
    updatedAt: ts,
    isLoading: true,
    isEnriching: false,
    error: null,
    activeRequestId: requestId,
  };
}

function reduceCollection(prev: EntityCollectionState, event: EntityEvent): EntityCollectionState {
  const { entityType, key } = (event as any);
  if (!entityType || !key) return prev;

  const record = ensureRecord(entityType, key, prev);

  if (event.type === 'entity/openRequested') {
    const e = event;
    const nextRecord =
      record
        ? { ...record, id: e.id ?? record.id, name: e.name ?? record.name, isLoading: true, error: null, activeRequestId: e.meta.requestId }
        : makeSkeleton(entityType, key, e.id, e.name, e.meta.requestId, e.meta.ts, e.hintKind);

    return {
      currentKey: key,
      byKey: { ...prev.byKey, [key]: nextRecord },
    };
  }

  // Ignore stale responses for the same key.
  if (record?.activeRequestId && record.activeRequestId !== event.meta.requestId) {
    // Monotonic upgrade rule:
    // If an older request returns *better* data for the same entity (common during WS reconnect/view echo),
    // do not drop it purely due to requestId mismatch.
    // Only drop stale events when they appear to target a different entity than the current record.
    if (
      event.type === 'entity/previewReceived' ||
      event.type === 'entity/dbReceived' ||
      event.type === 'entity/mbReceived' ||
      event.type === 'entity/enrichedReceived' ||
      event.type === 'entity/freshnessReceived' ||
      event.type === 'entity/batonPreloadReceived'
    ) {
      try {
        const payload: any = (event as any).payload || {};
        const payloadId = payload?.id || payload?.external_id || null;
        const recordId = record?.id || (record?.data as any)?.id || null;
        const payloadName = payload?.name || payload?.title || null;
        const recordName = record?.name || (record?.data as any)?.name || (record?.data as any)?.title || null;
        const sameName =
          !!payloadName &&
          !!recordName &&
          normalizeNameForCompare(payloadName) === normalizeNameForCompare(recordName);
        // If we can't compare ids, allow merge (merge logic is monotonic and won't regress fields).
        if (!payloadId || !recordId) {
          // allow through
        } else if (String(payloadId) !== String(recordId) && !sameName) {
          return prev;
        }
      } catch {
        // if uncertain, allow through (merge remains monotonic)
      }
    } else {
      return prev;
    }
  }

  if (
    event.type === 'entity/previewReceived' ||
    event.type === 'entity/dbReceived' ||
    event.type === 'entity/mbReceived' ||
    event.type === 'entity/enrichedReceived' ||
    event.type === 'entity/freshnessReceived' ||
    event.type === 'entity/batonPreloadReceived'
  ) {
    const e = event;
    let merged: EntityRecord;
    if (entityType === 'artist') merged = mergeArtistRecord(record, e.payload, e.meta, (record as any)?.kind);
    else if (entityType === 'person') merged = mergePersonRecord(record, e.payload, e.meta);
    else merged = mergeAlbumRecord(record, e.payload, e.meta);

    return {
      currentKey: prev.currentKey,
      byKey: { ...prev.byKey, [key]: merged },
    };
  }

  if (event.type === 'entity/loadingChanged') {
    const e = event;
    const nextRecord = record || makeSkeleton(entityType, key, null, null, e.meta.requestId, e.meta.ts);
    return {
      currentKey: prev.currentKey,
      byKey: { ...prev.byKey, [key]: { ...nextRecord, isLoading: e.value, updatedAt: e.meta.ts } },
    };
  }

  if (event.type === 'entity/enrichingChanged') {
    const e = event;
    const nextRecord = record || makeSkeleton(entityType, key, null, null, e.meta.requestId, e.meta.ts);
    return {
      currentKey: prev.currentKey,
      byKey: { ...prev.byKey, [key]: { ...nextRecord, isEnriching: e.value, updatedAt: e.meta.ts } },
    };
  }

  if (event.type === 'entity/error') {
    const e = event;
    const nextRecord = record || makeSkeleton(entityType, key, null, null, e.meta.requestId, e.meta.ts);
    return {
      currentKey: prev.currentKey,
      byKey: { ...prev.byKey, [key]: { ...nextRecord, error: e.error, isLoading: false, isEnriching: false, updatedAt: e.meta.ts } },
    };
  }

  return prev;
}

export function reduceArtistState(prev: EntityCollectionState, event: EntityEvent): EntityCollectionState {
  if (event.entityType !== 'artist') return prev;
  return reduceCollection(prev, event);
}

export function reducePersonState(prev: EntityCollectionState, event: EntityEvent): EntityCollectionState {
  if (event.entityType !== 'person') return prev;
  return reduceCollection(prev, event);
}

export function reduceAlbumState(prev: EntityCollectionState, event: EntityEvent): EntityCollectionState {
  if (event.entityType !== 'album') return prev;
  return reduceCollection(prev, event);
}

